import { StyleSheet } from "react-native";

/**
 * Design tokens & Tailwind‑like utility styles for the new design system.
 *
 * Usage examples:
 *   <View
 *     style={[
 *       backgroundColorStyles.bgBrand,
 *       spacingStyles.pMd,
 *       radiusStyles.roundedLg,
 *       shadowStyles.lg,
 *     ]}
 *   >
 *     <Text
 *       style={[
 *         typographyStyles.textLg,
 *         typographyStyles.fontSemibold,
 *         textColorStyles.textOnBrand,
 *       ]}
 *     >
 *       Button label
 *     </Text>
 *   </View>
 */

// Base color scales
export const colors = {
  grey: {
    25: "#fcfcfd",
    50: "#f9fafb",
    100: "#f2f4f7",
    200: "#eaecf0",
    300: "#d0d5dd",
    400: "#98a2b3",
    500: "#667085",
    600: "#475467",
    700: "#344054",
    800: "#182230",
    900: "#101828",
    950: "#0c111d",
  },
  brand: {
    25: "#f2f5ff",
    50: "#e6ebff",
    100: "#ccd7ff",
    200: "#9aa4ff",
    300: "#6788fe",
    400: "#5177fe",
    500: "#3e68fe",
    600: "#3e68fe",
    700: "#012298",
    800: "#011665",
    900: "#000b33",
    950: "#000619",
  },
  error: {
    25: "#fff8fa",
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
  },
  warning: {
    25: "#fff7ed",
    50: "#fff4eb",
    100: "#ffedd5",
    200: "#fed7aa",
    300: "#fdba74",
    400: "#f97316",
    500: "#f79009",
    600: "#dc6803",
    700: "#b54708",
    800: "#93370d",
    900: "#7a2e0e",
    950: "#4e1d09",
  },
  success: {
    25: "#f6fef9",
    50: "#ecfdf3",
    100: "#d1fadf",
    200: "#a6f4c5",
    300: "#6ce9a6",
    400: "#32d583",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16",
  },
  accent: {
    maroon100: "#fde8f0",
    maroon500: "#f64184",
    maroon700: "#c53469",
    purple100: "#e8ddfb",
    purple500: "#8e55ea",
    purple700: "#7244bb",
    teal100: "#d1e8eb",
    teal500: "#1a8e9d",
    teal700: "#15727d",
    ocean100: "#cce6ff",
    ocean500: "#0084ff",
    ocean700: "#0069cc",
  },
  white: "#ffffff",
  black: "#000000",
} as const;

// Web-only transition tokens (React Native Web will use these, native will ignore)
interface WebTransitionStyle {
  // These properties are used only on web; native platforms will ignore them.
  transitionProperty?: string;
  transitionDuration?: string;
  transitionTimingFunction?: string;
}

export const webTransitions: { fast: WebTransitionStyle } = {
  fast: {
    transitionProperty: "all",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease-out",
  },
};

// Semantic colors based on the design system
export const semanticColors = {
  surface: {
    white: colors.white,
    light: colors.grey[50],
    brand: colors.brand[500],
    brandLight: colors.brand[50],
    brandDark: colors.brand[800],
    brandXLight: colors.brand[25],
    error: colors.error[500],
    errorLight: colors.error[50],
    success: colors.success[500],
    successLight: colors.success[50],
    warning: colors.warning[300],
    warningLight: colors.warning[50],
    disabled: colors.grey[300],
    disabledDark: colors.grey[400],
    progress: colors.success[400],
    progressTrack: colors.grey[200],
    accentPurple: colors.accent.purple500,
    accentMaroon: colors.accent.maroon500,
    accentTeal: colors.accent.teal500,
    accentOcean: colors.accent.ocean500,
    transparent: "transparent",
  },
  typography: {
    textPrimary: colors.grey[700],
    textDark: colors.grey[800],
    textLight: colors.grey[500],
    textDisabled: colors.grey[400],
    textDisabledLight: colors.grey[200],
    textBrand: colors.brand[500],
    textBrandLight: colors.brand[50],
    textBrandDark: colors.brand[700],
    textError: colors.error[50],
    textErrorDark: colors.error[500],
    textSuccess: colors.success[50],
    textSuccessDark: colors.success[600],
    textWarning: colors.warning[50],
    textWarningDark: colors.warning[500],
    textWhite: colors.white,
    displayPrimary: colors.grey[900],
  },
} as const;

// Spacing scale (px)
export const spacing = {
  none: 0,
  xxs: 4,
  xs: 8,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  "2xl": 40,
  "3xl": 48,
  "4xl": 64,
} as const;

// Radius scale (px)
export const radii = {
  none: 0,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 16,
  xl: 32,
  full: 9999,
} as const;

// Typography scale
export const typographyScale = {
  displayXl: { fontSize: 48, lineHeight: 56 },
  displayLg: { fontSize: 36, lineHeight: 44 },
  displayMd: { fontSize: 30, lineHeight: 38 },
  displaySm: { fontSize: 24, lineHeight: 32 },
  textXl: { fontSize: 24, lineHeight: 32 },
  textLg: { fontSize: 20, lineHeight: 28 },
  textMd: { fontSize: 18, lineHeight: 26 },
  textBase: { fontSize: 16, lineHeight: 24 },
  textSm: { fontSize: 14, lineHeight: 20 },
  textXs: { fontSize: 12, lineHeight: 16 },
} as const;

// ---- Utility StyleSheets ----

export const backgroundColorStyles = StyleSheet.create({
  bgWhite: { backgroundColor: semanticColors.surface.white },
  bgLight: { backgroundColor: semanticColors.surface.light },
  bgBrand: { backgroundColor: semanticColors.surface.brand },
  bgBrandLight: { backgroundColor: semanticColors.surface.brandLight },
  bgBrandDark: { backgroundColor: semanticColors.surface.brandDark },
  bgBrandXLight: { backgroundColor: semanticColors.surface.brandXLight },
  bgError: { backgroundColor: semanticColors.surface.error },
  bgErrorLight: { backgroundColor: semanticColors.surface.errorLight },
  bgSuccess: { backgroundColor: semanticColors.surface.success },
  bgSuccessLight: { backgroundColor: semanticColors.surface.successLight },
  bgWarning: { backgroundColor: semanticColors.surface.warning },
  bgWarningLight: { backgroundColor: semanticColors.surface.warningLight },
  bgDisabled: { backgroundColor: semanticColors.surface.disabled },
  bgDisabledDark: { backgroundColor: semanticColors.surface.disabledDark },
  bgProgress: { backgroundColor: semanticColors.surface.progress },
  bgProgressTrack: { backgroundColor: semanticColors.surface.progressTrack },
  bgAccentPurple: { backgroundColor: semanticColors.surface.accentPurple },
  bgAccentMaroon: { backgroundColor: semanticColors.surface.accentMaroon },
  bgAccentTeal: { backgroundColor: semanticColors.surface.accentTeal },
  bgAccentOcean: { backgroundColor: semanticColors.surface.accentOcean },
  bgTransparent: { backgroundColor: semanticColors.surface.transparent },
});

export const textColorStyles = StyleSheet.create({
  textPrimary: { color: semanticColors.typography.textPrimary },
  textDark: { color: semanticColors.typography.textDark },
  textLight: { color: semanticColors.typography.textLight },
  textDisabled: { color: semanticColors.typography.textDisabled },
  textDisabledLight: { color: semanticColors.typography.textDisabledLight },
  textBrand: { color: semanticColors.typography.textBrand },
  textBrandLight: { color: semanticColors.typography.textBrandLight },
  textBrandDark: { color: semanticColors.typography.textBrandDark },
  textError: { color: semanticColors.typography.textError },
  textErrorDark: { color: semanticColors.typography.textErrorDark },
  textSuccess: { color: semanticColors.typography.textSuccess },
  textSuccessDark: { color: semanticColors.typography.textSuccessDark },
  textWarning: { color: semanticColors.typography.textWarning },
  textWarningDark: { color: semanticColors.typography.textWarningDark },
  textWhite: { color: semanticColors.typography.textWhite },
  displayPrimary: { color: semanticColors.typography.displayPrimary },
});

// Spacing utilities – Tailwind‑like padding & margin helpers
export const spacingStyles = StyleSheet.create({
  // padding
  pNone: { padding: spacing.none },
  pXxs: { padding: spacing.xxs },
  pXs: { padding: spacing.xs },
  pSm: { padding: spacing.sm },
  pMd: { padding: spacing.md },
  pLg: { padding: spacing.lg },
  pXl: { padding: spacing.xl },
  p2xl: { padding: spacing["2xl"] },
  p3xl: { padding: spacing["3xl"] },
  p4xl: { padding: spacing["4xl"] },

  // padding horizontal
  pxNone: { paddingHorizontal: spacing.none },
  pxXxs: { paddingHorizontal: spacing.xxs },
  pxXs: { paddingHorizontal: spacing.xs },
  pxSm: { paddingHorizontal: spacing.sm },
  pxMd: { paddingHorizontal: spacing.md },
  pxLg: { paddingHorizontal: spacing.lg },
  pxXl: { paddingHorizontal: spacing.xl },
  px2xl: { paddingHorizontal: spacing["2xl"] },
  px3xl: { paddingHorizontal: spacing["3xl"] },
  px4xl: { paddingHorizontal: spacing["4xl"] },

  // padding vertical
  pyNone: { paddingVertical: spacing.none },
  pyXxs: { paddingVertical: spacing.xxs },
  pyXs: { paddingVertical: spacing.xs },
  pySm: { paddingVertical: spacing.sm },
  pyMd: { paddingVertical: spacing.md },
  pyLg: { paddingVertical: spacing.lg },
  pyXl: { paddingVertical: spacing.xl },
  py2xl: { paddingVertical: spacing["2xl"] },
  py3xl: { paddingVertical: spacing["3xl"] },
  py4xl: { paddingVertical: spacing["4xl"] },

  // margin
  mNone: { margin: spacing.none },
  mXxs: { margin: spacing.xxs },
  mXs: { margin: spacing.xs },
  mSm: { margin: spacing.sm },
  mMd: { margin: spacing.md },
  mLg: { margin: spacing.lg },
  mXl: { margin: spacing.xl },
  m2xl: { margin: spacing["2xl"] },
  m3xl: { margin: spacing["3xl"] },
  m4xl: { margin: spacing["4xl"] },

  // margin horizontal
  mxNone: { marginHorizontal: spacing.none },
  mxXxs: { marginHorizontal: spacing.xxs },
  mxXs: { marginHorizontal: spacing.xs },
  mxSm: { marginHorizontal: spacing.sm },
  mxMd: { marginHorizontal: spacing.md },
  mxLg: { marginHorizontal: spacing.lg },
  mxXl: { marginHorizontal: spacing.xl },
  mx2xl: { marginHorizontal: spacing["2xl"] },
  mx3xl: { marginHorizontal: spacing["3xl"] },
  mx4xl: { marginHorizontal: spacing["4xl"] },

  // margin vertical
  myNone: { marginVertical: spacing.none },
  myXxs: { marginVertical: spacing.xxs },
  myXs: { marginVertical: spacing.xs },
  mySm: { marginVertical: spacing.sm },
  myMd: { marginVertical: spacing.md },
  myLg: { marginVertical: spacing.lg },
  myXl: { marginVertical: spacing.xl },
  my2xl: { marginVertical: spacing["2xl"] },
  my3xl: { marginVertical: spacing["3xl"] },
  my4xl: { marginVertical: spacing["4xl"] },
});

// Radius utilities
export const radiusStyles = StyleSheet.create({
  roundedNone: { borderRadius: radii.none },
  roundedXs: { borderRadius: radii.xs },
  roundedSm: { borderRadius: radii.sm },
  roundedMd: { borderRadius: radii.md },
  roundedLg: { borderRadius: radii.lg },
  roundedXl: { borderRadius: radii.xl },
  roundedFull: { borderRadius: radii.full },
});

// Shadow utilities – approximations of the multi‑layer CSS shadows in RN
export const shadowStyles = StyleSheet.create({
  none: {
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  xs: {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sm: {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  md: {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  lg: {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  xl: {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.14,
    shadowRadius: 24,
    elevation: 8,
  },
  "2xl": {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.16,
    shadowRadius: 32,
    elevation: 10,
  },
  "3xl": {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 32 },
    shadowOpacity: 0.18,
    shadowRadius: 40,
    elevation: 12,
  },
});

// Border utilities
export const borderStyles = StyleSheet.create({
  border: {
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  borderMuted: {
    borderWidth: 1,
    borderColor: colors.grey[300],
  },
  borderStrong: {
    borderWidth: 1,
    borderColor: colors.grey[400],
  },
  borderBrand: {
    borderWidth: 1,
    borderColor: colors.brand[500],
  },
  borderError: {
    borderWidth: 1,
    borderColor: colors.error[500],
  },
  borderSuccess: {
    borderWidth: 1,
    borderColor: colors.success[500],
  },
  borderWarning: {
    borderWidth: 1,
    borderColor: colors.warning[500],
  },
  borderTransparent: {
    borderWidth: 1,
    borderColor: "transparent",
  },
});

// Typography utilities – font sizes + weights based on the scale
export const typographyStyles = StyleSheet.create({
  displayXl: {
    fontSize: typographyScale.displayXl.fontSize,
    lineHeight: typographyScale.displayXl.lineHeight,
    fontWeight: "700",
  },
  displayLg: {
    fontSize: typographyScale.displayLg.fontSize,
    lineHeight: typographyScale.displayLg.lineHeight,
    fontWeight: "700",
  },
  displayMd: {
    fontSize: typographyScale.displayMd.fontSize,
    lineHeight: typographyScale.displayMd.lineHeight,
    fontWeight: "700",
  },
  displaySm: {
    fontSize: typographyScale.displaySm.fontSize,
    lineHeight: typographyScale.displaySm.lineHeight,
    fontWeight: "700",
  },
  textXl: {
    fontSize: typographyScale.textXl.fontSize,
    lineHeight: typographyScale.textXl.lineHeight,
    fontWeight: "400",
  },
  textLg: {
    fontSize: typographyScale.textLg.fontSize,
    lineHeight: typographyScale.textLg.lineHeight,
    fontWeight: "400",
  },
  textMd: {
    fontSize: typographyScale.textMd.fontSize,
    lineHeight: typographyScale.textMd.lineHeight,
    fontWeight: "400",
  },
  textBase: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "400",
  },
  textSm: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
  textXs: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "400",
  },

  // font weight helpers to compose with any of the above
  fontRegular: { fontWeight: "400" },
  fontSemibold: { fontWeight: "600" },
  fontBold: { fontWeight: "700" },
});
